"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import AppLogo from "@/components/ui/AppLogo";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const navLinks = [
    { label: "Platform", href: "#platform" },
    { label: "Agents", href: "#agents" },
    { label: "Integrations", href: "#integrations" },
    { label: "Security", href: "#security" },
    { label: "Contact", href: "/contact" },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-[rgba(11,15,26,0.92)] backdrop-blur-xl border-b border-white/[0.06]"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center gap-2.5 group">
            <AppLogo size={140} />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks?.map((link) => (
              <a
                key={link?.label}
                href={link?.href}
                className="nav-link text-sm font-medium text-[#8892A4] hover:text-white transition-colors duration-200"
              >
                {link?.label}
              </a>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://app.myndlayer.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost text-sm"
            >Sign In</a>
            <a
              href="https://app.myndlayer.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-teal text-sm"
            >
              Get Access Now
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 text-[#8892A4] hover:text-white transition-colors"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation menu"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        </div>
      </header>
      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-[100] transition-all duration-300 ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        style={{ background: "rgba(11,15,26,0.98)", backdropFilter: "blur(20px)" }}
      >
        <div className="flex flex-col h-full px-6 py-6">
          {/* Header row */}
          <div className="flex items-center justify-between mb-12">
            <div className="flex items-center gap-2.5">
              <AppLogo size={140} />
            </div>
            <button
              onClick={() => setMobileOpen(false)}
              className="p-2 text-[#8892A4] hover:text-white"
              aria-label="Close navigation menu"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Nav links */}
          <nav className="flex flex-col gap-6 flex-1">
            {navLinks?.map((link) => (
              <a
                key={link?.label}
                href={link?.href}
                onClick={() => setMobileOpen(false)}
                className="text-2xl font-display font-medium text-white hover:text-[#0FF4C6] transition-colors"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                {link?.label}
              </a>
            ))}
          </nav>

          {/* Mobile CTAs */}
          <div className="flex flex-col gap-3 pb-8">
            <a
              href="https://app.myndlayer.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost w-full text-center"
            >Sign In</a>
            <a
              href="https://app.myndlayer.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-teal w-full text-center"
            >
              Get Access Now
            </a>
          </div>
        </div>
      </div>
    </>
  );
}